import { createContext, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { savePrefs, useDashboardPrefs, type DashboardSurface } from '@/lib/use-dashboard-prefs';
import {
    clampSize,
    type StoredWidgetEntry,
    type WidgetHeight,
    type WidgetWidth,
} from '@/components/my-page/grid/widget-sizes';
import { clampPosition } from '@/components/my-page/grid/placement';

export type WidgetId =
    // Desk>My Page — the signed-in IM's own live KPI slice. Every figure is the
    // caller's own book; tiles also carry "my % of firm" against firm totals.
    // KPIs are computed live from the ValuationGrid (AUM/counts/YTD); flows,
    // income and performance have no live source (see docs/kpi-gaps.md).
    | 'desk_my_page_aum_tile'
    // My AUA tile (Advisory + Execution-Only) — companion to My AUM (Discretionary)
    | 'desk_my_page_aua_tile'
    | 'desk_my_page_ytd_tile'
    | 'desk_my_page_clients_tile'
    | 'desk_my_page_portfolios_tile'
    // My per-category breakdown bars
    | 'desk_my_page_aum_by_category'
    // My per-category AUM-weighted YTD bars
    | 'desk_my_page_ytd_by_category'
    // Across-month AUM trend (accrues from each ValuationGrid ingest)
    | 'desk_my_page_aum_trend'
    // Donut + my-categories table
    | 'desk_my_page_category_donut'
    | 'desk_my_page_categories_table'
    // Comply — synthetic demo widget catalogue (Comply>My Page)
    | 'comply_breach_status'
    | 'comply_kyc_expiry'
    | 'comply_tc_completion'
    | 'comply_consumer_duty'
    | 'comply_smcr_status'
    // View>KPI — firm-wide live KPI widget catalogue.
    | 'view_kpi_aum_tile'
    | 'view_kpi_ytd_tile'
    | 'view_kpi_clients_tile'
    | 'view_kpi_portfolios_tile'
    // Per-IM breakdown bars
    | 'view_kpi_aum_by_im'
    | 'view_kpi_aum_by_category'
    // Across-month AUM trend
    | 'view_kpi_aum_trend'
    // Donut + league table
    | 'view_kpi_category_donut'
    | 'view_kpi_league';

export type Frontend = 'desk' | 'ops' | 'comply' | 'view';

export interface PinnedWidget {
    id: WidgetId;
    label: string;
    frontend: Frontend;
    pinnedAt: number;
    /**
     * The widget's footprint on the grid, in grid cells (issue #215). Both are
     * optional: a pin minted before a size was chosen, or rehydrated from a legacy
     * saved layout, carries no `w`/`h` and falls back to its kind's catalogue
     * default at render (`resolveSize`).
     */
    w?: WidgetWidth;
    h?: WidgetHeight;
    /**
     * The widget's absolute cell position on the Android-style grid (1-based).
     * Optional: a pin with no position (legacy layout, or just added) is
     * auto-placed into the first free cell at render (`resolveLayout`); a drag
     * stamps the chosen `(x, y)`.
     */
    x?: number;
    y?: number;
}

/**
 * A widget id paired with its persisted footprint + position — the shape that
 * survives the round-trip through the prefs API. `id` is narrowed to a known
 * `WidgetId`; the generic `StoredWidgetEntry` (off the wire) keeps `id` a bare
 * string.
 */
export interface PinnedEntry {
    id: WidgetId;
    w?: WidgetWidth;
    h?: WidgetHeight;
    x?: number;
    y?: number;
}

/**
 * Per-frontend dashboard surface key (ADR-0009). Maps the in-app `frontend`
 * the pins are grouped by onto the opaque persistence `surface` the server
 * keys layouts on. Only the surfaces in the server's allow-list persist; a
 * frontend absent here keeps its pins in memory only (forward-compatible —
 * #190/#191 extend the catalogue and the allow-list together).
 */
const FRONTEND_SURFACE: Partial<Record<Frontend, DashboardSurface>> = {
    desk: 'desk_my_page',
    view: 'view_kpi',
};

/**
 * Per-surface default-pinned widget set (ADR-0009 / PRD #185).
 *
 * Applied only on the `present: false` path — when the signed-in user has never
 * saved a layout for this surface, these ids are the starting pin set so the
 * page is useful out of the box. Once the user pins / unpins anything the saved
 * row takes over (the surface is then `present: true` on every later load and
 * this default is never re-applied). Exported so it can be unit-tested.
 */
export const SURFACE_DEFAULT_WIDGETS: Partial<Record<DashboardSurface, WidgetId[]>> = {
    view_kpi: [
        'view_kpi_aum_tile',
        'view_kpi_ytd_tile',
        'view_kpi_clients_tile',
        'view_kpi_league',
    ],
    // A fuller starting layout (feedback p10 — "feels empty"): a hero stat row
    // (AUM, AUA, YTD, clients, portfolios) over a grouped chart block (AUM- and
    // YTD-by-category bars, AUM trend, the proportional donut) and the
    // per-category table. Order here is the canvas order on first load.
    desk_my_page: [
        'desk_my_page_aum_tile',
        'desk_my_page_aua_tile',
        'desk_my_page_ytd_tile',
        'desk_my_page_clients_tile',
        'desk_my_page_portfolios_tile',
        'desk_my_page_aum_by_category',
        'desk_my_page_ytd_by_category',
        'desk_my_page_aum_trend',
        'desk_my_page_category_donut',
        'desk_my_page_categories_table',
    ],
};

/**
 * Resolve the sized entry list a surface should start from on first load: the
 * server-saved order (with each entry's persisted footprint) when the user has
 * a row, otherwise the surface default (ids only — every default widget renders
 * at its kind's catalogue size). Pure so the default-vs-saved branch is
 * unit-testable without a DOM.
 */
export function resolveInitialEntries(
    surface: DashboardSurface,
    present: boolean,
    savedEntries: ReadonlyArray<StoredWidgetEntry>,
): PinnedEntry[] {
    if (present) {
        return savedEntries.map((e) => ({
            id: e.id as WidgetId,
            w: e.w as WidgetWidth | undefined,
            h: e.h as WidgetHeight | undefined,
            x: e.x,
            y: e.y,
        }));
    }
    return (SURFACE_DEFAULT_WIDGETS[surface] ?? []).map((id) => ({ id }));
}

/**
 * Pure reducer: replace one frontend's pins with the server-saved entry order.
 *
 * This is the heart of reload-survival (ADR-0009) — when a remounted provider
 * loads a saved layout, this rebuilds that frontend's slice of the pin set from
 * the ordered entries, carrying each entry's persisted footprint (`w`/`h`) and
 * reusing any matching in-memory pin's LABEL. Pins on other frontends are
 * untouched. Exported so it can be unit-tested without a DOM.
 */
export function hydrateFrontendPins(
    prev: PinnedWidget[],
    frontend: Frontend,
    entries: ReadonlyArray<PinnedEntry>,
): PinnedWidget[] {
    const others = prev.filter((w) => w.frontend !== frontend);
    const rehydrated: PinnedWidget[] = entries.map((entry, i) => {
        const existing = prev.find((w) => w.id === entry.id);
        return {
            id: entry.id,
            label: existing?.label ?? entry.id,
            frontend,
            pinnedAt: existing?.pinnedAt ?? i,
            w: entry.w,
            h: entry.h,
            x: entry.x,
            y: entry.y,
        };
    });
    return [...others, ...rehydrated];
}

/**
 * Pure reposition reducer: stamp an absolute cell position `(x, y)` onto ONE pin
 * (edit-mode drag on the Android-style grid). The caller (drag handler) has
 * already snapped `(x, y)` to a cell, clamped it on-grid, and verified it does
 * not collide — this just writes it in place. A position equal to the pin's
 * current one is a no-op (returns `prev`, same reference); an unknown id too.
 * Exported for unit tests.
 */
export function setPositionForPin(
    prev: PinnedWidget[],
    id: WidgetId,
    x: number,
    y: number,
): PinnedWidget[] {
    const target = prev.find((p) => p.id === id);
    if (!target) return prev;
    if (target.x === x && target.y === y) return prev;
    return prev.map((p) => (p.id === id ? { ...p, x, y } : p));
}

/**
 * Pure bulk-position reducer: stamp positions onto several pins at once. Used by
 * the shell to MATERIALISE auto-placed positions after load — every pin lacking
 * a stored `(x, y)` (a legacy layout, a freshly added widget) gets the position
 * `resolveLayout` computed, so the model becomes fully positioned in one render.
 * Returns `prev` unchanged (same reference) when nothing actually changes, so the
 * materialise effect settles after one pass instead of looping. Exported for
 * unit tests.
 */
export function setPositionsForPins(
    prev: PinnedWidget[],
    positions: ReadonlyArray<{ id: WidgetId; x: number; y: number }>,
): PinnedWidget[] {
    const byId = new Map(positions.map((p) => [p.id, p]));
    let changed = false;
    const next = prev.map((p) => {
        const pos = byId.get(p.id);
        if (!pos || (p.x === pos.x && p.y === pos.y)) return p;
        changed = true;
        return { ...p, x: pos.x, y: pos.y };
    });
    return changed ? next : prev;
}

/**
 * Pure resize reducer: write a FREE-FORM footprint onto ONE pin (edit-mode
 * corner grip). The grip drags the widget to any `(w, h)` within the grid bounds
 * — there is no preset cycle — and this stamps the new footprint in place,
 * re-clamping the pin's position so a grown widget never overflows the grid. The
 * caller is expected to have clamped `(w, h)` already (`clampSize`); a footprint
 * that equals the pin's current one is a no-op (returns `prev`, same reference)
 * so a drag that crosses no cell boundary triggers no re-render and the editing
 * `dirty` check stays honest. An unknown id is also a no-op. Exported for unit
 * tests.
 */
export function setSizeForPin(
    prev: PinnedWidget[],
    id: WidgetId,
    w: WidgetWidth,
    h: WidgetHeight,
): PinnedWidget[] {
    const target = prev.find((p) => p.id === id);
    if (!target) return prev;
    if (target.w === w && target.h === h) return prev;
    // Keep a positioned widget on-grid after it grows (e.g. a 1-wide widget at
    // x=4 widened to 2 would overflow). Unpositioned pins stay unpositioned.
    const pos =
        target.x != null && target.y != null ? clampPosition(target.x, target.y, w, h) : null;
    return prev.map((p) =>
        p.id === id ? { ...p, w, h, ...(pos ? { x: pos.x, y: pos.y } : {}) } : p,
    );
}

interface MyPageContextValue {
    pinned: PinnedWidget[];
    pin: (widget: PinnedWidget) => void;
    unpin: (id: WidgetId) => void;
    isPinned: (id: WidgetId) => boolean;
    /**
     * Move one pin to an absolute cell `(x, y)` on the Android-style grid
     * (edit-mode drag). The caller has snapped + bounds-clamped + collision-
     * checked the target. Live on the canvas; persists on Save like every
     * edit-mode mutation (the mirror is suspended while editing).
     */
    setPosition: (id: WidgetId, x: number, y: number) => void;
    /**
     * Materialise auto-placed positions: stamp `(x, y)` onto pins that had none
     * (legacy layouts, freshly added widgets) so the model is fully positioned.
     * Called by the shell after `resolveLayout`; a no-op when nothing changes.
     */
    setPositions: (positions: ReadonlyArray<{ id: WidgetId; x: number; y: number }>) => void;
    /**
     * Write a free-form footprint onto one pin (edit-mode corner grip). `(w, h)`
     * are grid-cell counts; the context clamps them to the grid bounds
     * (`clampSize`) and re-clamps the pin's position so a grown widget stays
     * on-grid. Live during a drag — the change shows on the canvas at once — but,
     * like every edit-mode mutation, it only PERSISTS on Save (the mirror is
     * suspended while the pin's frontend is being edited).
     */
    setSize: (id: WidgetId, w: WidgetWidth, h: WidgetHeight) => void;

    // ─── Edit mode (buffered Save/Cancel) ────────────────────────────────────
    //
    // A widget canvas is read-only until the user clicks Edit; arranging tools
    // (drag / remove / resize / library) only show while editing. Edits apply to
    // `pinned` LIVE so the canvas previews them, but the `SurfaceSync` mirror is
    // SUSPENDED for the editing frontend, so nothing is written until Save. Save
    // persists the current footprints; Cancel reverts to the snapshot taken when
    // Edit began. Non-edit mutations (e.g. the AI panel's "Add to My Page") still
    // persist live, because the mirror is only suspended for the editing frontend.

    /** The frontend currently in edit mode, or null. Only one at a time. */
    editingFrontend: Frontend | null;
    /** True while a Save (PUT) is in flight — drives the Save button's busy state. */
    saving: boolean;
    /** True when the editing frontend's footprints differ from the Edit snapshot. */
    dirty: boolean;
    /** Enter edit mode for a frontend, snapshotting its current layout for Cancel. */
    beginEdit: (frontend: Frontend) => void;
    /** Persist the editing frontend's layout, then leave edit mode. */
    saveEdit: (frontend: Frontend) => Promise<void>;
    /** Discard edits — restore the Edit snapshot — and leave edit mode. */
    cancelEdit: (frontend: Frontend) => void;
}

const MyPageContext = createContext<MyPageContextValue | null>(null);

/**
 * One persisted surface's slice of the global pin set. Mounted once per
 * frontend that has a backing surface; loads that surface's saved widget-id
 * order on mount and writes it back (debounced) whenever this frontend's pins
 * change — UNLESS the frontend is being edited, in which case the mirror is
 * suspended so buffered edits persist only on Save (the provider's `saveEdit`
 * writes them directly). It owns no rendered UI — it is a persistence sidecar.
 */
function SurfaceSync({
    frontend,
    surface,
    pinned,
    suspended,
    onHydrate,
}: {
    frontend: Frontend;
    surface: DashboardSurface;
    pinned: PinnedWidget[];
    /** True while this frontend is in edit mode — pauses the live mirror. */
    suspended: boolean;
    onHydrate: (frontend: Frontend, entries: PinnedEntry[]) => void;
}) {
    const { state, setWidgets } = useDashboardPrefs(surface);

    // On first server load, hydrate the in-memory pin set for this frontend.
    // A saved row hydrates its id order (reload survival); an absent row
    // (`present: false`) hydrates the surface default so the page is useful out
    // of the box. After hydration the mirror effect below writes the resolved
    // ids back, materialising the default as the user's starting layout.
    const hydratedRef = useRef(false);
    useEffect(() => {
        if (!state.loaded || hydratedRef.current) return;
        hydratedRef.current = true;
        const initial = resolveInitialEntries(surface, state.present, state.widgets);
        if (initial.length > 0) {
            onHydrate(frontend, initial);
        }
    }, [state.loaded, state.present, state.widgets, surface, frontend, onHydrate]);

    // After hydration, mirror every change to this frontend's pins back to the
    // server as the ordered sized-entry list — except while suspended (edit
    // mode), when the provider's Save is the sole writer. `suspended` is read but
    // deliberately NOT a dep: leaving edit mode must not itself re-fire a write
    // (Save already persisted; Cancel restored the server-equal snapshot), so the
    // mirror only resumes on the NEXT real layout change.
    const entries = useMemo(() => entriesFor(pinned, frontend), [pinned, frontend]);
    const entriesKey = entryKey(entries);
    useEffect(() => {
        if (!hydratedRef.current || suspended) return;
        setWidgets(entries);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entriesKey]);

    return null;
}

/** Serialise this frontend's pins to the ordered entry list to persist. */
function entriesFor(pinned: PinnedWidget[], frontend: Frontend): StoredWidgetEntry[] {
    return pinned
        .filter((w) => w.frontend === frontend)
        .map((w) => ({
            id: w.id,
            ...(w.w !== undefined ? { w: w.w } : {}),
            ...(w.h !== undefined ? { h: w.h } : {}),
            ...(w.x !== undefined ? { x: w.x } : {}),
            ...(w.y !== undefined ? { y: w.y } : {}),
        }));
}

/**
 * Stable serialisation of an entry list (id + footprint + position, in order).
 * Two lists with the same key represent the same persisted layout — used to
 * dedupe mirror writes and to compute the edit-mode `dirty` flag against the
 * snapshot.
 */
function entryKey(entries: ReadonlyArray<StoredWidgetEntry>): string {
    return entries
        .map((e) => `${e.id}:${e.w ?? ''}:${e.h ?? ''}:${e.x ?? ''}:${e.y ?? ''}`)
        .join(' ');
}

/** The persistence surface backing a frontend, or undefined if in-memory only. */
export function surfaceFor(frontend: Frontend): DashboardSurface | undefined {
    return FRONTEND_SURFACE[frontend];
}

export function MyPageProvider({ children }: { children: ReactNode }) {
    const [pinned, setPinned] = useState<PinnedWidget[]>([]);
    // Edit-mode state (buffered Save/Cancel). At most one frontend edits at a
    // time (one my-page route is mounted at once). The snapshot is this
    // frontend's layout at the moment Edit began, used to revert on Cancel and to
    // compute `dirty`.
    const [editingFrontend, setEditingFrontend] = useState<Frontend | null>(null);
    const [saving, setSaving] = useState(false);
    const editSnapshotRef = useRef<StoredWidgetEntry[]>([]);

    const pin = (widget: PinnedWidget) => {
        setPinned((prev) => (prev.find((p) => p.id === widget.id) ? prev : [...prev, widget]));
    };

    const unpin = (id: WidgetId) => {
        setPinned((prev) => prev.filter((p) => p.id !== id));
    };

    const isPinned = (id: WidgetId) => pinned.some((p) => p.id === id);

    const setPosition = (id: WidgetId, x: number, y: number) => {
        setPinned((prev) => setPositionForPin(prev, id, x, y));
    };

    const setPositions = (positions: ReadonlyArray<{ id: WidgetId; x: number; y: number }>) => {
        setPinned((prev) => setPositionsForPins(prev, positions));
    };

    const setSize = (id: WidgetId, w: WidgetWidth, h: WidgetHeight) => {
        const clamped = clampSize(w, h);
        setPinned((prev) => setSizeForPin(prev, id, clamped.w, clamped.h));
    };

    // Replace a frontend's pins with the server-saved entry order on hydration.
    // Labels are not persisted (the server stores ids + sizes + positions only);
    // a placeholder label is carried until the consuming page's widget catalogue
    // resolves the real one by id (MyPageShell rehydrates labels from its config).
    // Each entry's footprint (w/h) and position (x/y) carries through.
    const hydrate = (frontend: Frontend, entries: PinnedEntry[]) => {
        setPinned((prev) => hydrateFrontendPins(prev, frontend, entries));
    };

    // ─── Edit mode ───────────────────────────────────────────────────────────

    const beginEdit = (frontend: Frontend) => {
        editSnapshotRef.current = entriesFor(pinned, frontend);
        setEditingFrontend(frontend);
    };

    const saveEdit = async (frontend: Frontend) => {
        const surface = surfaceFor(frontend);
        if (surface) {
            setSaving(true);
            try {
                await savePrefs(surface, entriesFor(pinned, frontend));
            } finally {
                setSaving(false);
            }
        }
        setEditingFrontend(null);
    };

    const cancelEdit = (frontend: Frontend) => {
        const snapshot = editSnapshotRef.current.map(
            (e): PinnedEntry => ({
                id: e.id as WidgetId,
                w: e.w as WidgetWidth | undefined,
                h: e.h as WidgetHeight | undefined,
                x: e.x,
                y: e.y,
            }),
        );
        setPinned((prev) => hydrateFrontendPins(prev, frontend, snapshot));
        setEditingFrontend(null);
    };

    // Dirty when the editing frontend's live footprints diverge from the snapshot
    // taken at Edit. False whenever nothing is being edited.
    const dirty =
        editingFrontend !== null &&
        entryKey(entriesFor(pinned, editingFrontend)) !== entryKey(editSnapshotRef.current);

    return (
        <MyPageContext.Provider
            value={{
                pinned,
                pin,
                unpin,
                isPinned,
                setPosition,
                setPositions,
                setSize,
                editingFrontend,
                saving,
                dirty,
                beginEdit,
                saveEdit,
                cancelEdit,
            }}
        >
            {Object.entries(FRONTEND_SURFACE).map(([frontend, surface]) => (
                <SurfaceSync
                    key={frontend}
                    frontend={frontend as Frontend}
                    surface={surface}
                    pinned={pinned}
                    suspended={editingFrontend === frontend}
                    onHydrate={hydrate}
                />
            ))}
            {children}
        </MyPageContext.Provider>
    );
}

export function useMyPage() {
    const ctx = useContext(MyPageContext);
    if (!ctx) throw new Error('useMyPage must be used within MyPageProvider');
    return ctx;
}
